import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Cores do site
const primary = '#34574A'; // verde principal
const accent = '#c0843d';  // dourado/acento

const FloatingButton = styled.button`
  position: fixed;
  left: 20px;
  bottom: 20px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: ${primary};
  color: #fff;
  box-shadow: 0 6px 16px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;
  transition: transform .15s ease, box-shadow .15s ease, background .2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 18px rgba(0,0,0,0.25); background: #2c4a3f; }
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 1100;
`;

const ModalCard = styled.div`
  width: min(860px, 96vw);
  max-height: 90vh;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(0,0,0,.25);
  display: flex; flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 18px 20px; border-bottom: 1px solid #eee;
  font-size: 18px; font-weight: 700; color: #1f2937;
`;

const ModalBody = styled.div`
  padding: 8px 20px 0; overflow: auto;
`;

const ModalFooter = styled.div`
  padding: 14px 20px; border-top: 1px solid #eee; display: flex; gap: 10px; justify-content: end;
`;

// Banner fino no rodapé para primeira visita
const BannerWrap = styled.div`
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 1200;
  background: rgba(255,255,255,.88);
  border-top: 1px solid rgba(229,231,235,.8);
  box-shadow: 0 -6px 18px rgba(0,0,0,.06);
  backdrop-filter: blur(6px);
`;
const BannerInner = styled.div`
  max-width: 1100px; margin: 0 auto; padding: 8px 12px; display: flex; align-items: center; justify-content: space-between;
  column-gap: 12px; row-gap: 6px; flex-wrap: wrap;
  font-size: 14px; color: #374151; min-height: 44px;
`;
const BannerActions = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap;
`;

const Category = styled.details`
  margin: 8px 0;
  summary {
    cursor: pointer; font-weight: 700; color: #111827; display: flex; align-items: center; justify-content: space-between;
    list-style: none; background: #f5f7f9; border: 1px solid #eef0f2; border-radius: 10px; padding: 12px 14px;
    white-space: nowrap;
  }
  summary::-webkit-details-marker { display: none; }
  summary::marker { content: ""; display: none; }
  /* caret control via element */
  .caret { display:inline-block; margin-right:10px; transform: rotate(-90deg); transition: transform .2s ease; color:#6b7280; }
  &[open] .caret { transform: rotate(0deg); }
  p { margin: 12px 2px 4px; color: #4b5563; line-height: 1.5; font-weight: 400; padding: 0 2px; }
`;

const Pill = styled.span`
  font-size: 12px; font-weight: 700; border-radius: 999px; padding: 2px 8px; margin-left: 10px;
  background: ${props => props.$on ? '#ecfdf5' : '#fff7ed'};
  color: ${props => props.$on ? '#16a34a' : '#c2410c'};
  border: 1px solid ${props => props.$on ? '#bbf7d0' : '#fed7aa'};
`;

const Row = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px; width: 100%;
  .cc-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const StatusText = styled.span`
  font-weight: 700; font-size: 14px;
  color: ${props => props.$on ? '#16a34a' : '#c2410c'};
`;

const Toggle = styled.label`
  position: relative; width: 48px; height: 28px; display: inline-block;
  input { opacity:0; width:0; height:0; }
  span { position:absolute; cursor:pointer; inset:0; background:#e5e7eb; border-radius:999px; transition: .2s; }
  span:before { content:""; position:absolute; height:22px; width:22px; left:3px; top:3px; background:white; border-radius:50%; transition:.2s; box-shadow: 0 1px 2px rgba(0,0,0,.2); }
  input:checked + span { background:${primary}; }
  input:checked + span:before { transform: translateX(20px); }
  input:disabled + span { background:#e5e7eb; opacity:.7; cursor:not-allowed; }
`;

const Ghost = styled.button`
  border: 1px solid #e5e7eb; background:#fff; color:#111827; padding:10px 14px; border-radius:8px; font-weight:600; cursor:pointer;
  &:hover { background:#f9fafb; }
`;
const Primary = styled.button`
  border: none; background:${primary}; color:#fff; padding:10px 16px; border-radius:8px; font-weight:700; cursor:pointer;
  &:hover { background:#2c4a3f; }
`;
const Accent = styled.button`
  border: none; background:${accent}; color:#fff; padding:10px 16px; border-radius:8px; font-weight:700; cursor:pointer;
  &:hover { filter:brightness(.95); }
`;

const cookieKey = 'cookie_consent_v1';

export default function CookieConsent() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(() => ({ necessary: true, functional: false, analytics: false, performance: false, ads: false }));
  const [hasConsent, setHasConsent] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(cookieKey);
      if (raw) {
        const saved = JSON.parse(raw);
        setPrefs({ necessary: true, functional: !!saved.functional, analytics: !!saved.analytics, performance: !!saved.performance, ads: !!saved.ads });
        setHasConsent(true);
      } else {
        // Primeira visita: mostrar apenas banner compacto no rodapé
        setShowBanner(true);
      }
    } catch { setOpen(true); }
  }, []);

  useEffect(() => {
    // trava scroll quando modal aberto
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const allOn = useMemo(() => prefs.functional && prefs.analytics && prefs.performance && prefs.ads, [prefs]);

  const acceptAll = () => {
    const next = { necessary: true, functional: true, analytics: true, performance: true, ads: true };
    setPrefs(next);
    localStorage.setItem(cookieKey, JSON.stringify(next));
    setHasConsent(true);
    setOpen(false);
    setShowBanner(false);
  };
  const rejectAll = () => {
    const next = { necessary: true, functional: false, analytics: false, performance: false, ads: false };
    setPrefs(next);
    localStorage.setItem(cookieKey, JSON.stringify(next));
    setHasConsent(true);
    setOpen(false);
    setShowBanner(false);
  };
  const savePrefs = () => {
    localStorage.setItem(cookieKey, JSON.stringify(prefs));
    setHasConsent(true);
    setOpen(false);
    setShowBanner(false);
  };

  return (
    <>
      {/* Botão flutuante sempre visível após consentir; escondido se modal aberto */}
      {!open && (
        <FloatingButton title={t('cookie.floatingButtonTitle')} onClick={() => setOpen(true)} aria-label={t('cookie.floatingButtonTitle')}>
          {/* ícone cookie + check minimalista */}
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2a10 10 0 1 0 8.66 15.11A4 4 0 0 1 17 12a4 4 0 0 1-4-4 4 4 0 0 1-1-2A10 10 0 0 0 12 2Z" fill="currentColor"/>
            <path d="M9.5 13.5l1.8 1.8 3.7-3.7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </FloatingButton>
      )}

      {/* Banner compacto no primeiro acesso */}
      {showBanner && !hasConsent && !open && (
        <BannerWrap role="region" aria-label="Aviso de cookies">
          <BannerInner>
            <div style={{flex:1, paddingRight:8, whiteSpace:'normal'}}>
            {t('cookie.banner.text')}
            </div>
            <a href="/cookie-policy" target="_blank" rel="noreferrer" style={{color: primary, fontWeight:600, flexShrink:0, whiteSpace:'nowrap'}}>
              {t('cookie.banner.policyLink')}
            </a>
            <BannerActions>
              <Ghost onClick={() => { setShowBanner(false); setOpen(true); }}>{t('cookie.banner.preferences')}</Ghost>
              <Primary onClick={acceptAll}>{t('cookie.banner.acceptAll')}</Primary>
            </BannerActions>
          </BannerInner>
        </BannerWrap>
      )}

      {open && (
        <ModalOverlay role="dialog" aria-modal="true">
          <ModalCard>
            <ModalHeader>{t('cookie.modal.title')}</ModalHeader>
            <ModalBody>
              <p style={{padding:'0 0 14px', color:'#4b5563'}}>
                {t('cookie.modal.intro1')}
              </p>
              <p style={{padding:'0 0 14px', color:'#4b5563'}}>
                {t('cookie.modal.intro2')}
              </p>

              <Category>
                <summary>
                  <Row>
                    <div className="cc-title" style={{display:'flex',alignItems:'center',gap:10}}>
                      <span className="caret">▾</span>
                      <span>{t('cookie.categories.necessary.title')}</span>
                    </div>
                    <Toggle>
                      <input type="checkbox" checked readOnly disabled />
                      <span/>
                    </Toggle>
                  </Row>
                </summary>
                <p>
                  {t('cookie.categories.necessary.description')}
                </p>
              </Category>

              <Category>
                <summary>
                  <Row>
                    <div className="cc-title" style={{display:'flex',alignItems:'center',gap:10}}>
                      <span className="caret">▾</span>
                      <span>{t('cookie.categories.functional.title')}</span>
                    </div>
                    <Toggle>
                      <input type="checkbox" checked={prefs.functional} onChange={e => setPrefs(p => ({...p, functional: e.target.checked}))} />
                      <span/>
                    </Toggle>
                  </Row>
                </summary>
                <p>
                  {t('cookie.categories.functional.description')}
                </p>
              </Category>

              <Category>
                <summary>
                  <Row>
                    <div className="cc-title" style={{display:'flex',alignItems:'center',gap:10}}>
                      <span className="caret">▾</span>
                      <span>{t('cookie.categories.analytics.title')}</span>
                    </div>
                    <Toggle>
                      <input type="checkbox" checked={prefs.analytics} onChange={e => setPrefs(p => ({...p, analytics: e.target.checked}))} />
                      <span/>
                    </Toggle>
                  </Row>
                </summary>
                <p>
                  {t('cookie.categories.analytics.description')}
                </p>
              </Category>

              <Category>
                <summary>
                  <Row>
                    <div className="cc-title" style={{display:'flex',alignItems:'center',gap:10}}>
                      <span className="caret">▾</span>
                      <span>{t('cookie.categories.performance.title')}</span>
                    </div>
                    <Toggle>
                      <input type="checkbox" checked={prefs.performance} onChange={e => setPrefs(p => ({...p, performance: e.target.checked}))} />
                      <span/>
                    </Toggle>
                  </Row>
                </summary>
                <p>
                  {t('cookie.categories.performance.description')}
                </p>
              </Category>

              <Category>
                <summary>
                  <Row>
                    <div className="cc-title" style={{display:'flex',alignItems:'center',gap:10}}>
                      <span className="caret">▾</span>
                      <span>{t('cookie.categories.ads.title')}</span>
                    </div>
                    <Toggle>
                      <input type="checkbox" checked={prefs.ads} onChange={e => setPrefs(p => ({...p, ads: e.target.checked}))} />
                      <span/>
                    </Toggle>
                  </Row>
                </summary>
                <p>
                  {t('cookie.categories.ads.description')}
                </p>
              </Category>
            </ModalBody>
            <ModalFooter>
              <Ghost onClick={rejectAll}>{t('cookie.modal.reject')}</Ghost>
              <Ghost onClick={savePrefs}>{t('cookie.modal.save')}</Ghost>
              <Primary onClick={acceptAll}>{t('cookie.modal.accept')}</Primary>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
}
